import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Search, ExternalLink, Trash2, Inbox, RefreshCw, ToggleLeft, ToggleRight, Sparkles, Loader2, Check, AlertTriangle, UserX, UserCheck, MessageSquare } from "lucide-react";
import { API, ROLE_OPTIONS, STATUS_STYLES, ROLE_BADGE_STYLES, authHeaders } from "@/constants/roles";
import { usePerms } from "@/components/PermsContext";
import { TestPanel } from "@/components/admin/TestPanel";
import { ApplicantChat } from "@/components/hiring/ApplicantChat";

const STATUSES = ["pending", "reviewing", "accepted", "rejected"];
const FILTERS = ["All", ...ROLE_OPTIONS];

const scoreStyle = (score) =>
  score >= 8
    ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
    : score >= 5
      ? "bg-amber-500/15 text-amber-300 border-amber-500/30"
      : "bg-rose-500/15 text-rose-300 border-rose-500/30";

const RECOMMENDATION_STYLES = {
  interview: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  maybe: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  pass: "bg-rose-500/15 text-rose-300 border-rose-500/30",
};

export const AdminApplications = () => {
  const { has, role } = usePerms();
  const [applications, setApplications] = useState([]);
  const [tests, setTests] = useState({});
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [reviewingId, setReviewingId] = useState(null);
  const [chatOpenFor, setChatOpenFor] = useState(null);
  const navigate = useNavigate();

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [appsRes, rolesRes, testsRes] = await Promise.all([
        axios.get(`${API}/applications`, { headers: authHeaders() }),
        axios.get(`${API}/roles`),
        axios.get(`${API}/staff/test-assignments`, { headers: authHeaders() }).catch(() => ({ data: [] })),
      ]);
      setApplications(appsRes.data);
      setRoles(rolesRes.data);
      const byApp = {};
      (testsRes.data || []).forEach((t) => {
        if (!byApp[t.application_id] || (t.created_at || "") > (byApp[t.application_id].created_at || "")) byApp[t.application_id] = t;
      });
      setTests(byApp);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        toast.error("Session expired. Log in again.");
        localStorage.removeItem("melto_token");
        navigate("/login");
      } else {
        toast.error("Could not load applications.");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const toggleRole = async (role, open) => {
    try {
      await axios.patch(`${API}/roles/status`, { role, open }, { headers: authHeaders() });
      setRoles((rs) => rs.map((r) => (r.role === role ? { ...r, open } : r)));
      toast.success(`${role} applications ${open ? "opened" : "closed"}.`);
    } catch {
      toast.error("Could not update role status.");
    }
  };

  const toggleWorker = async (app) => {
    const deactivated = !app.worker_deactivated;
    try {
      await axios.patch(`${API}/admin/users/${app.user_id}/status`, { deactivated }, { headers: authHeaders() });
      setApplications((apps) => apps.map((a) => (a.id === app.id ? { ...a, worker_deactivated: deactivated } : a)));
      toast.success(deactivated ? "Worker production access disabled." : "Worker reactivated.");
    } catch {
      toast.error("Could not update worker access.");
    }
  };

  const requestReview = async (id) => {
    setReviewingId(id);
    try {
      const { data } = await axios.post(`${API}/applications/${id}/ai-review`, {}, { headers: authHeaders() });
      setApplications((apps) => apps.map((a) => (a.id === id ? { ...a, ai_review: data } : a)));
      toast.success("AI review ready.");
    } catch {
      toast.error("AI review failed. Try again.");
    } finally {
      setReviewingId(null);
    }
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return applications.filter((a) => {
      if (roleFilter !== "All" && a.role !== roleFilter) return false;
      if (q && !a.name.toLowerCase().includes(q) && !a.discord.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [applications, roleFilter, search]);

  const counts = useMemo(() => {
    const c = { total: applications.length, pending: 0, reviewing: 0, accepted: 0 };
    applications.forEach((a) => {
      if (c[a.status] !== undefined) c[a.status] += 1;
    });
    return c;
  }, [applications]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/applications/${id}/status`, { status }, { headers: authHeaders() });
      setApplications((apps) => apps.map((a) => (a.id === id ? { ...a, status } : a)));
      toast.success(
        status === "accepted"
          ? "Accepted — account promoted to worker, welcome email sent."
          : status === "reviewing"
            ? "Marked as reviewing — applicant notified by email."
            : `Marked as ${status}`
      );
    } catch {
      toast.error("Could not update status.");
    }
  };

  const saveReview = async (id, patch) => {
    try {
      const { data } = await axios.patch(`${API}/applications/${id}/review`, patch, { headers: authHeaders() });
      setApplications((apps) => apps.map((a) => (a.id === id ? { ...a, ...data } : a)));
      toast.success("Review saved.");
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not save review.");
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/applications/${id}`, { headers: authHeaders() });
      setApplications((apps) => apps.filter((a) => a.id !== id));
      toast.success("Application deleted.");
    } catch {
      toast.error("Could not delete application.");
    }
  };

  return (
    <div data-testid="admin-applications">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-[var(--pv-text)]">Applications</h1>
          <p className="mt-1 text-sm text-[var(--pv-text-4)]">Review applicants, run AI reviews, and promote accepted crew.</p>
        </div>
        <button
          data-testid="applications-refresh-btn"
          onClick={fetchAll}
          className="flex items-center gap-1.5 rounded-full bg-[var(--pv-card)] border border-[var(--pv-border)] px-3.5 py-2 text-xs font-semibold text-[var(--pv-text-4)] transition-colors hover:bg-[var(--pv-subtle)]"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
        {[
          { label: "Total", value: counts.total, testid: "stat-total" },
          { label: "Pending", value: counts.pending, testid: "stat-pending" },
          { label: "Reviewing", value: counts.reviewing, testid: "stat-reviewing" },
          { label: "Accepted", value: counts.accepted, testid: "stat-accepted" },
        ].map((s) => (
          <div key={s.label} data-testid={s.testid} className="rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-6 ">
            <p className="font-display text-3xl font-bold tracking-tight text-[var(--pv-text)]">{s.value}</p>
            <p className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-[var(--pv-text-4)]">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-6 ">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--pv-text-4)]">Role availability</p>
        <div className="mt-4 flex flex-wrap gap-3">
          {roles.map((r) => (
            <button
              key={r.role}
              data-testid={`role-toggle-${r.role.toLowerCase().replace(/[\s/]+/g, "-")}`}
              onClick={() => toggleRole(r.role, !r.open)}
              className={`flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-bold transition-colors ${
                r.open
                  ? "border-emerald-500/20 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/15"
                  : "border-rose-500/20 bg-rose-500/10 text-rose-400 hover:bg-rose-500/15"
              }`}
            >
              {r.open ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
              {r.role} · {r.open ? "Open" : "Closed"}
            </button>
          ))}
        </div>
        <p className="mt-3 text-xs text-[var(--pv-text-4)]">Closed roles immediately stop accepting new applications on the site.</p>
      </div>

      <div className="mt-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`filter-${f.toLowerCase().replace(/[\s/]+/g, "-")}`}
              onClick={() => setRoleFilter(f)}
              className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${
                roleFilter === f
                  ? "bg-cyan-500 text-white shadow-md shadow-cyan-500/25"
                  : "bg-[var(--pv-card)] text-[var(--pv-text-4)] border border-[var(--pv-border)] hover:border-cyan-500/30"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
        <div className="relative lg:w-72">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--pv-text-4)]" />
          <input
            data-testid="admin-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name or Discord..."
            className="w-full rounded-full border border-[var(--pv-border)] bg-[var(--pv-card)] py-2.5 pl-11 pr-4 text-sm font-medium text-[var(--pv-text)] placeholder:text-[var(--pv-text-4)] focus:border-cyan-400 focus:outline-none focus:ring-4 focus:ring-cyan-500/10"
          />
        </div>
      </div>

      <div data-testid="admin-dashboard-table" className="mt-8 space-y-4">
        {loading ? (
          <p data-testid="applications-loading" className="py-16 text-center text-sm font-medium text-[var(--pv-text-4)]">
            Loading applications...
          </p>
        ) : visible.length === 0 ? (
          <div data-testid="applications-empty" className="flex flex-col items-center gap-3 rounded-3xl border border-dashed border-[var(--pv-border-strong)] bg-[var(--pv-card)] py-16">
            <Inbox className="h-10 w-10 text-[var(--pv-text-2)]" />
            <p className="text-sm font-medium text-[var(--pv-text-4)]">No applications here yet.</p>
          </div>
        ) : (
          visible.map((a) => (
            <article
              key={a.id}
              data-testid={`application-card-${a.id}`}
              className="rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-6  transition-shadow hover:shadow-md"
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="flex flex-wrap items-center gap-2.5">
                    <h3 data-testid={`application-name-${a.id}`} className="font-display text-lg font-semibold text-[var(--pv-text)]">
                      {a.name}
                    </h3>
                    <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-bold ${ROLE_BADGE_STYLES[a.role] || ROLE_BADGE_STYLES["General / Multiple Roles"]}`}>
                      {a.role}
                    </span>
                    <span
                      data-testid={`application-status-${a.id}`}
                      className={`rounded-full border px-2.5 py-0.5 text-[11px] font-bold uppercase ${STATUS_STYLES[a.status]}`}
                    >
                      {a.status}
                    </span>
                    {a.worker_deactivated && (
                      <span data-testid={`worker-deactivated-badge-${a.id}`} className="rounded-full border border-rose-500/30 bg-rose-500/15 px-2.5 py-0.5 text-[11px] font-bold uppercase text-rose-300">
                        Access disabled
                      </span>
                    )}
                  </div>
                  <p className="mt-1.5 text-xs font-medium text-[var(--pv-text-4)]">
                    {a.email && <span className="text-[var(--pv-text-2)]">{a.email}</span>}
                    {a.email && <span className="mx-2 text-[var(--pv-text-2)]">|</span>}
                    Discord: <span className="text-[var(--pv-text-2)]">{a.discord}</span>
                    <span className="mx-2 text-[var(--pv-text-2)]">|</span>
                    {new Date(a.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    data-testid={`ai-review-btn-${a.id}`}
                    onClick={() => requestReview(a.id)}
                    disabled={reviewingId === a.id}
                    className="flex items-center gap-1.5 rounded-full bg-cyan-500 px-3.5 py-1.5 text-xs font-bold text-white  shadow-cyan-500/25 transition-colors hover:bg-cyan-600 disabled:opacity-60"
                  >
                    {reviewingId === a.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                    {a.ai_review ? "Re-review" : "AI Review"}
                  </button>
                  {has("final_hire") ? (
                    <select
                      data-testid={`status-select-${a.id}`}
                      value={a.status}
                      onChange={(e) => updateStatus(a.id, e.target.value)}
                      className="rounded-full border border-[var(--pv-border)] bg-[var(--pv-card)] px-3.5 py-1.5 text-xs font-semibold text-[var(--pv-text-2)] focus:border-cyan-400 focus:outline-none"
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  ) : (
                    <span data-testid={`status-readonly-${a.id}`} className="rounded-full border border-[var(--pv-border)] bg-[var(--pv-subtle)] px-3.5 py-1.5 text-xs font-semibold text-[var(--pv-text-3)]">{a.status}</span>
                  )}
                  {has("recommend_applicants") && (
                    <select
                      data-testid={`recommend-select-${a.id}`}
                      value={a.recommendation || ""}
                      onChange={(e) => saveReview(a.id, { recommendation: e.target.value || null })}
                      className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold focus:border-cyan-400 focus:outline-none ${
                        a.recommendation === "recommended_for_hire" ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                        : a.recommendation === "not_recommended" ? "border-rose-500/30 bg-rose-500/10 text-rose-300"
                        : a.recommendation ? "border-amber-500/30 bg-amber-500/10 text-amber-300"
                        : "border-[var(--pv-border)] bg-[var(--pv-card)] text-[var(--pv-text-4)]"
                      }`}
                    >
                      <option value="">Recommend…</option>
                      <option value="recommended_for_hire">Recommended for hire</option>
                      <option value="not_recommended">Not recommended</option>
                      <option value="needs_test">Needs test</option>
                      <option value="needs_owner_review">Needs owner review</option>
                    </select>
                  )}
                  {has("message_applicants") && (
                    <button
                      data-testid={`chat-toggle-${a.id}`}
                      onClick={() => setChatOpenFor((cur) => (cur === a.id ? null : a.id))}
                      className={`relative flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-bold transition-colors ${
                        chatOpenFor === a.id
                          ? "border-cyan-500/40 bg-cyan-500/15 text-cyan-300"
                          : "border-[var(--pv-border)] bg-[var(--pv-card)] text-[var(--pv-text-2)] hover:text-cyan-400"
                      }`}
                      title="Message the applicant — clarify expectations before deciding"
                    >
                      <MessageSquare className="h-3.5 w-3.5" /> Chat
                      {(a.chat_unread || 0) > 0 && (
                        <span data-testid={`chat-unread-${a.id}`} className="flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[9px] font-bold text-white">
                          {a.chat_unread}
                        </span>
                      )}
                    </button>
                  )}
                  {a.user_id && a.status === "accepted" && has("deactivate_worker") && (
                    <button
                      data-testid={`worker-toggle-${a.id}`}
                      onClick={() => toggleWorker(a)}
                      className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${
                        a.worker_deactivated
                          ? "bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/15"
                          : "bg-rose-500/10 text-rose-400 hover:bg-rose-500/15"
                      }`}
                    >
                      {a.worker_deactivated ? <UserCheck className="h-3.5 w-3.5" /> : <UserX className="h-3.5 w-3.5" />}
                      {a.worker_deactivated ? "Reactivate" : "Deactivate"}
                    </button>
                  )}
                  {has("reject_permanently") && (
                    <button
                      data-testid={`delete-application-${a.id}`}
                      onClick={() => remove(a.id)}
                      className="rounded-full bg-rose-500/10 p-2 text-rose-500 transition-colors hover:bg-rose-500/15"
                      aria-label="Delete application"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>

              {has("recommend_applicants") && (
                <div className="mt-3 flex items-center gap-2">
                  <input
                    data-testid={`review-notes-${a.id}`}
                    defaultValue={a.review_notes || ""}
                    key={`${a.id}-${a.review_notes || ""}`}
                    placeholder="Internal review notes (team only)…"
                    onKeyDown={(e) => { if (e.key === "Enter") saveReview(a.id, { review_notes: e.target.value }); }}
                    className="min-w-0 flex-1 rounded-full border border-[var(--pv-border)] bg-[var(--pv-subtle)] px-3.5 py-1.5 text-xs text-[var(--pv-text-2)] placeholder:text-[var(--pv-text-4)] focus:border-cyan-400 focus:outline-none"
                  />
                  <button
                    data-testid={`review-notes-save-${a.id}`}
                    onClick={(e) => saveReview(a.id, { review_notes: e.currentTarget.previousSibling.value })}
                    className="rounded-full bg-[var(--pv-raised)] px-3 py-1.5 text-[11px] font-bold text-[var(--pv-text-2)] hover:text-cyan-400"
                  >
                    Save
                  </button>
                </div>
              )}

              {a.status !== "accepted" && (
                <TestPanel
                  app={a}
                  test={tests[a.id]}
                  isOwner={role === "admin"}
                  canManage={has("recommend_applicants")}
                  canHire={has("final_hire")}
                  onChanged={fetchAll}
                />
              )}

              {chatOpenFor === a.id && (
                <div className="mt-4">
                  <ApplicantChat applicationId={a.id} variant="staff" applicantName={a.name} />
                </div>
              )}

              <p className="mt-4 text-sm leading-relaxed text-[var(--pv-text-4)]">{a.message}</p>
              <a
                data-testid={`portfolio-link-${a.id}`}
                href={a.portfolio_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-cyan-400 transition-colors hover:text-cyan-300"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                {a.portfolio_url}
              </a>

              {a.ai_review && (
                <div data-testid={`ai-review-panel-${a.id}`} className="mt-5 rounded-2xl border border-cyan-500/20 bg-cyan-500/10 p-5">
                  <div className="flex flex-wrap items-center gap-2.5">
                    <span className="flex items-center gap-1.5 font-mono-label text-[10px] font-bold uppercase tracking-widest text-cyan-300">
                      <Sparkles className="h-3.5 w-3.5" /> Gemini review
                    </span>
                    <span data-testid={`ai-score-${a.id}`} className={`rounded-full border px-2.5 py-0.5 text-[11px] font-bold ${scoreStyle(a.ai_review.fit_score)}`}>
                      Fit {a.ai_review.fit_score}/10
                    </span>
                    {a.ai_review.recommendation && (
                      <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-bold uppercase ${RECOMMENDATION_STYLES[a.ai_review.recommendation] || RECOMMENDATION_STYLES.maybe}`}>
                        {a.ai_review.recommendation}
                      </span>
                    )}
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-[var(--pv-text-2)]">{a.ai_review.summary}</p>
                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-400">Strengths</p>
                      <ul className="mt-2 space-y-1.5">
                        {(a.ai_review.strengths || []).map((s) => (
                          <li key={s} className="flex items-start gap-2 text-xs text-[var(--pv-text-4)]">
                            <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" />
                            {s}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-wider text-amber-400">Concerns</p>
                      <ul className="mt-2 space-y-1.5">
                        {(a.ai_review.concerns || []).map((c) => (
                          <li key={c} className="flex items-start gap-2 text-xs text-[var(--pv-text-4)]">
                            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
                            {c}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </article>
          ))
        )}
      </div>
    </div>
  );
};
